import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { toast } from "sonner";
import { LOGO_IMG } from "@/lib/brand";
import { ShieldCheck } from "@phosphor-icons/react";

export default function AdminLogin() {
  const { adminLogin, adminSetup } = useAuth();
  const [mode, setMode] = useState("login"); // "login" | "setup"
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = mode === "setup"
        ? await adminSetup(form.email, form.password)
        : await adminLogin(form.email, form.password);
      toast.success(mode === "setup" ? "Admin account ready — welcome!" : `Welcome, ${user.name}`);
      nav("/admin/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy-900 flex items-center justify-center p-6">
      <div className="absolute inset-0 grain opacity-40" />
      <div className="relative w-full max-w-md bg-white border border-slate-200 p-10 shadow-2xl" data-testid="admin-auth-card">
        <img src={LOGO_IMG} alt="PMI" className="h-12 w-auto mb-4" />
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-amber-600">
          <ShieldCheck size={16} weight="fill" /> Internal Access
        </div>
        <h1 className="font-display text-3xl tracking-tight mt-3 text-navy-900">PMI Master Administration</h1>
        <p className="text-sm text-slate-500 mt-2 mb-8">{mode === "setup" ? "First-time setup — create or reset the PMI owner password." : "Authorized PMI staff only. All access is logged."}</p>
        <form onSubmit={submit} className="space-y-4">
          <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="Staff email" data-testid="admin-auth-email"
            className="w-full border border-slate-300 px-4 py-3 focus:ring-2 focus:ring-navy-900 focus:outline-none" />
          <input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder={mode === "setup" ? "New password (min 8 chars)" : "Password"} data-testid="admin-auth-password"
            className="w-full border border-slate-300 px-4 py-3 focus:ring-2 focus:ring-navy-900 focus:outline-none" />
          <button type="submit" disabled={loading} data-testid="admin-auth-submit"
            className="w-full bg-navy-900 text-white py-4 font-[600] hover:bg-emerald-700 transition-colors duration-200 disabled:opacity-60">
            {loading ? "Please wait…" : mode === "setup" ? "Create / Reset Admin" : "Access Console"}
          </button>
        </form>
        <button type="button" onClick={() => setMode(mode === "setup" ? "login" : "setup")} data-testid="admin-setup-toggle"
          className="mt-5 text-xs font-[600] text-slate-500 hover:text-emerald-700 transition-colors">
          {mode === "setup" ? "← Back to sign in" : "First time setup / Reset admin password"}
        </button>
      </div>
    </div>
  );
}
